import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, Login, PersonAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useInvitations } from '../contexts/InvitationsContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { formatDate } from '../utils/format';

interface InvitationDetails {
  id: string;
  email: string;
  role: 'host' | 'guest';
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  house: {
    id: string;
    name: string;
    description?: string;
    address?: string;
    host: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

const InvitationLandingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { refreshPendingInvitations } = useInvitations();
  const { refreshNotificationCount } = useNotifications();
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`/invitations/public/${id}`);
        setInvitation(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || t('invitationLanding.failedFetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [id, t]);

  const handleAction = async (action: 'accept' | 'reject') => {
    if (!id) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);
      await axios.post(`/invitations/${id}/${action}`);
      setInvitation((current) => current ? {
        ...current,
        status: action === 'accept' ? 'accepted' : 'rejected',
      } : current);
      await refreshPendingInvitations();
      await refreshNotificationCount();
    } catch (err: any) {
      setError(err.response?.data?.message || t(`invitationLanding.failed${action === 'accept' ? 'Accept' : 'Reject'}`));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAuthRedirect = (target: '/login' | '/register') => {
    if (!invitation || !id) {
      return;
    }

    const params = new URLSearchParams({
      redirect: `/invitations/${id}`,
      email: invitation.email,
    });
    navigate(`${target}?${params.toString()}`);
  };

  const isMatchingUser = user && invitation
    ? user.email.toLowerCase() === invitation.email.toLowerCase()
    : false;

  return (
    <Layout title={t('invitationLanding.layoutTitle')}>
      <Container maxWidth="sm">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error || !invitation ? (
          <Alert severity="error">{error || t('invitationLanding.notFound')}</Alert>
        ) : (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                <Typography variant="h5">{invitation.house.name}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip label={t(`common.roles.${invitation.role}`)} variant="outlined" size="small" />
                  <Chip label={t(`common.statuses.${invitation.status}`)} color={invitation.status === 'pending' ? 'warning' : invitation.status === 'accepted' ? 'success' : 'default'} size="small" />
                </Box>
              </Box>

              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('invitationLanding.subtitle', { houseName: invitation.house.name, role: t(`common.roles.${invitation.role}`).toLowerCase() })}
              </Typography>

              {invitation.house.description && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  {invitation.house.description}
                </Typography>
              )}

              {invitation.house.address && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('invitationLanding.location')}:</strong> {invitation.house.address}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{t('common.labels.host')}:</strong> {invitation.house.host.firstName} {invitation.house.host.lastName}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{t('invitationLanding.invitedEmail')}:</strong> {invitation.email}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                {t('common.labels.invitedOn', { date: formatDate(invitation.createdAt, i18n.resolvedLanguage) })}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mt: 3 }}>
                  {error}
                </Alert>
              )}

              {!isAuthenticated && invitation.status === 'pending' && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  {t('invitationLanding.loginRequired')}
                </Alert>
              )}

              {isAuthenticated && !isMatchingUser && invitation.status === 'pending' && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  {t('invitationLanding.wrongAccount', { email: invitation.email })}
                </Alert>
              )}

              {invitation.status !== 'pending' && (
                <Alert severity={invitation.status === 'accepted' ? 'success' : 'info'} sx={{ mt: 3 }}>
                  {invitation.status === 'accepted'
                    ? t('invitationLanding.accepted')
                    : t('invitationLanding.rejected')}
                </Alert>
              )}
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
              {!isAuthenticated && invitation.status === 'pending' && (
                <>
                  <Button startIcon={<Login />} variant="outlined" onClick={() => handleAuthRedirect('/login')}>
                    {t('invitationLanding.login')}
                  </Button>
                  <Button startIcon={<PersonAdd />} variant="contained" onClick={() => handleAuthRedirect('/register')}>
                    {t('invitationLanding.register')}
                  </Button>
                </>
              )}

              {isAuthenticated && isMatchingUser && invitation.status === 'pending' && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    disabled={actionLoading}
                    onClick={() => handleAction('reject')}
                  >
                    {t('common.actions.decline')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    disabled={actionLoading}
                    onClick={() => handleAction('accept')}
                  >
                    {t('common.actions.accept')}
                  </Button>
                </>
              )}

              <Button onClick={() => navigate(isAuthenticated ? '/invitations' : '/')}>
                {t('invitationLanding.back')}
              </Button>
            </CardActions>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default InvitationLandingPage;